import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { useAuthStore, usePermissionsStore } from '@rfacil/shared/services';

/**
 * @hook useRefrescoPermisos
 * @description Mantiene al día los permisos del usuario con sesión. Los guards siguen leyendo el
 * store persistido (sus valores se usan en cuanto abre la app) y este hook lo refresca en segundo
 * plano:
 * - al abrir la app con la sesión ya iniciada (y al iniciar sesión, donde se suma a la carga del login);
 * - al cambiar de pantalla y al volver a la pestaña (foco o visibilidad), solo si la última carga
 *   exitosa tiene más de `VIGENCIA_PERMISOS_MS`; el store decide la vigencia y evita pedidos simultáneos.
 *
 * Sin sesión no escucha ni pide nada: al cerrar sesión se quitan los listeners.
 * Se monta una sola vez, dentro del router (`AppContent`).
 */
export function useRefrescoPermisos(): void {
  const autenticado = useAuthStore((s) => s.isAuthenticated);
  const { pathname } = useLocation();

  useEffect(() => {
    if (!autenticado) return;
    void usePermissionsStore.getState().refrescarPermisos();
  }, [autenticado, pathname]);

  useEffect(() => {
    if (!autenticado) return;
    const alVolverALaPestana = () => {
      if (document.visibilityState === 'hidden') return;
      void usePermissionsStore.getState().refrescarPermisos();
    };
    window.addEventListener('focus', alVolverALaPestana);
    document.addEventListener('visibilitychange', alVolverALaPestana);
    return () => {
      window.removeEventListener('focus', alVolverALaPestana);
      document.removeEventListener('visibilitychange', alVolverALaPestana);
    };
  }, [autenticado]);
}
